import { useCallback } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import { useAuth } from '../context/authContext';
import { getDeviceDescriptor } from '../util/deviceInfo';

// Hook for the device type request flow:
// - users request that their current device be turned into a device type
// - IT staff review, approve and reject those requests
// - devices exchange their temporary request token for a real device token
export default function useDeviceTypeRequest() {
  const { sendRequest } = useHttp();
  const { getAuthHeaders } = useAuth();

  // Create a new request for the current device. Returns { request, requestDeviceToken }.
  // `authToken` exists for the device-type picker on the login screen: no session
  // is established there yet, so the request travels with the temporary login
  // token instead of the stored one (same as useDeviceToken does).
  const requestDeviceType = useCallback(
    async ({ requestedDeviceType, reason, label, authToken }) => {
      try {
        const descriptor = getDeviceDescriptor();
        const res = await sendRequest({
          url: '/api/v1/deviceTypeRequest',
          method: 'POST',
          headers: authToken
            ? { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` }
            : getAuthHeaders(),
          body: {
            requestedDeviceType,
            reason: reason || undefined,
            label: label || undefined,
            installId: descriptor.installId,
            userAgent: descriptor.userAgent,
            platform: descriptor.platform,
          },
        });
        return res;
      } catch (error) {
        toast.error('Fehler beim Einreichen der Gerätetyp-Anfrage');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // The current user's own requests (for the settings status display).
  const getMyDeviceTypeRequests = useCallback(async () => {
    try {
      const res = await sendRequest({
        url: '/api/v1/deviceTypeRequest/me',
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return Array.isArray(res?.results) ? res.results : [];
    } catch (error) {
      toast.error('Fehler beim Laden der eigenen Gerätetyp-Anfragen');
      throw error;
    }
  }, [getAuthHeaders, sendRequest]);

  // IT review list.
  const getDeviceTypeRequests = useCallback(
    async (status = 'pending') => {
      const params = {};
      if (status) params.status = status;
      try {
        const res = await sendRequest({
          url: '/api/v1/deviceTypeRequest',
          method: 'GET',
          headers: getAuthHeaders(),
          params,
        });
        return Array.isArray(res?.results) ? res.results : [];
      } catch (error) {
        toast.error('Fehler beim Laden der Gerätetyp-Anfragen');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const approveDeviceTypeRequest = useCallback(
    async requestId => {
      try {
        return await sendRequest({
          url: `/api/v1/deviceTypeRequest/${requestId}/approve`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: {},
        });
      } catch (error) {
        toast.error('Fehler beim Freigeben der Anfrage');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  const rejectDeviceTypeRequest = useCallback(
    async (requestId, reason = '') => {
      const body = {};
      if (reason) body.reason = reason;
      try {
        return await sendRequest({
          url: `/api/v1/deviceTypeRequest/${requestId}/reject`,
          method: 'POST',
          headers: getAuthHeaders(),
          body,
        });
      } catch (error) {
        toast.error('Fehler beim Ablehnen der Anfrage');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // The current user's own registered devices.
  const getMyDevices = useCallback(async () => {
    try {
      const res = await sendRequest({
        url: '/api/v1/deviceTypeRequest/me/devices',
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return Array.isArray(res?.results) ? res.results : [];
    } catch (error) {
      toast.error('Fehler beim Laden der registrierten Geräte');
      throw error;
    }
  }, [getAuthHeaders, sendRequest]);

  // Revoke one of the current user's own devices.
  const revokeMyDevice = useCallback(
    async deviceId => {
      try {
        return await sendRequest({
          url: `/api/v1/deviceTypeRequest/me/devices/${deviceId}`,
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
      } catch (error) {
        toast.error('Fehler beim Entfernen des Geräts');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // IT: registered devices, optionally filtered by user / including revoked.
  const getDevices = useCallback(
    async ({ userId, includeRevoked } = {}) => {
      const params = {};
      if (userId) params.userId = userId;
      if (includeRevoked) params.includeRevoked = 'true';
      try {
        const res = await sendRequest({
          url: '/api/v1/deviceTypeRequest/devices',
          method: 'GET',
          headers: getAuthHeaders(),
          params,
        });
        return Array.isArray(res?.results) ? res.results : [];
      } catch (error) {
        toast.error('Fehler beim Laden der registrierten Geräte');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  // IT: revoke a registered device.
  const revokeDevice = useCallback(
    async deviceId => {
      try {
        return await sendRequest({
          url: `/api/v1/deviceTypeRequest/devices/${deviceId}/revoke`,
          method: 'POST',
          headers: getAuthHeaders(),
          body: {},
        });
      } catch (error) {
        toast.error('Fehler beim Widerrufen des Geräts');
        throw error;
      }
    },
    [getAuthHeaders, sendRequest]
  );

  return {
    requestDeviceType,
    getMyDeviceTypeRequests,
    getDeviceTypeRequests,
    approveDeviceTypeRequest,
    rejectDeviceTypeRequest,
    getMyDevices,
    revokeMyDevice,
    getDevices,
    revokeDevice,
  };
}
